<template>
    <div>
        <v-data-table
            id="virtual-scroll-table"
            disable-pagination
            hide-default-footer
            :headers="tableHeaders"
            :items="event.elements"
            item-key="packingList.id"
            class="elevation-0"
            :mobile-breakpoint="0"
            fixed-header
            :height="$vuetify.breakpoint.mdAndDown ? '70vh' : '70vh'"
            :loading="loading"
            multiple-expand
            :expanded.sync="expanded"
            show-expand
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pb-0 align-center"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex align-center">
                        <h2 class="ml-4">
                            Packing Lists
                        </h2>
                        <v-spacer />
                        <v-btn
                            color="secondary"
                            class="mr-4"
                            @click="goToPackingBalance"
                        >
                            <v-icon left>mdi-package-variant</v-icon>
                            Go to Packing Balance
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{
                            item.project
                                ? `${item.project.name || ''} - ${item.project
                                      .reference || ''} `
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.packingList`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{ item.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.progressPacking`]="{}">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        100%
                    </p>
                </div>
            </template>
            <template v-slot:[`item.workOrders`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{
                            item.packingList.selectedWorkOrders
                                ? item.packingList.selectedWorkOrders
                                      .map(wo => wo.code)
                                      .join(', ')
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.container`]="{ item }">
                <div class="d-flex justify-center align-center">
                    <p class="my-0 text-capitalize mr-2">
                        {{ item.packingList.container || '-' }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.requestedShipmentDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{
                            item.packingList.requestedShipmentDate
                                ? item.packingList.requestedShipmentDate
                                : '-'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.scheduledFor`]="{ item }">
                <div class="d-flex justify-center align-center">
                    <p class="my-0 text-capitalize mr-2">
                        {{
                            item.packingList.scheduledFor
                                ? item.packingList.scheduledFor
                                : '-'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.airShipment`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{ item.packingList.airShipment ? 'Yes' : 'No' }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0 text-capitalize">
                        {{
                            item.packingList.status
                                ? item.packingList.status
                                : '-'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.productionProgress`]="{ item }">
                <div
                    class="d-flex flex-column align-center justify-center px-2"
                >
                    <v-progress-linear
                        :value="item.productionProgressPL || 0"
                        :color="
                            getProgressColor(
                                'progress',
                                item.productionProgressPL || 0
                            )
                        "
                        height="20"
                        rounded
                    >
                        <template v-slot:default>
                            <strong class="white--text">
                                {{
                                    Math.round(item.productionProgressPL || 0)
                                }}%
                            </strong>
                        </template>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.releaseProgress`]="{ item }">
                <div
                    class="d-flex flex-column align-center justify-center px-2"
                >
                    <v-progress-linear
                        :value="item.releaseProgressPL || 0"
                        :color="
                            getProgressColor(
                                'progress',
                                item.releaseProgressPL || 0
                            )
                        "
                        height="20"
                        rounded
                    >
                        <template v-slot:default>
                            <strong class="white--text">
                                {{ Math.round(item.releaseProgressPL || 0) }}%
                            </strong>
                        </template>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.packingProgress`]="{ item }">
                <div
                    class="d-flex flex-column align-center justify-center px-2"
                >
                    <v-progress-linear
                        :value="item.packingProgressPL || 0"
                        :color="
                            getProgressColor(
                                'progress',
                                item.packingProgressPL || 0
                            )
                        "
                        height="20"
                        rounded
                    >
                        <template v-slot:default>
                            <strong class="white--text">
                                {{ Math.round(item.packingProgressPL || 0) }}%
                            </strong>
                        </template>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.important`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn
                        v-if="canToggleImportant(item)"
                        icon
                        small
                        @click="toggleImportant(item)"
                        :loading="item.loadingImportant"
                    >
                        <v-icon
                            :color="item.packingList.important ? 'red' : 'grey'"
                        >
                            {{
                                item.packingList.important
                                    ? 'mdi-flag'
                                    : 'mdi-flag-outline'
                            }}
                        </v-icon>
                    </v-btn>
                    <v-icon
                        v-else-if="item.packingList.important"
                        color="red"
                        small
                    >
                        mdi-flag
                    </v-icon>
                    <span v-else>-</span>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-btn
                                    icon
                                    small
                                    :disabled="
                                        getPLStatus(
                                            item.packingList,
                                            item.project
                                        ) !== 'reserved'
                                    "
                                    @click="openDeletePackingListDialog(item)"
                                >
                                    <v-icon color="error">mdi-delete</v-icon>
                                </v-btn>
                            </span>
                        </template>
                        <span>
                            {{
                                getPLStatus(item.packingList, item.project) ===
                                'reserved'
                                    ? 'Delete packing list'
                                    : 'Packing list must be in reserved status'
                            }}
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template
                v-slot:expanded-item="{
                    headers,
                    item: item,
                }"
            >
                <td
                    :colspan="headers.length"
                    class="pa-4 ma-0 outlined"
                    :style="{ background: 'white' }"
                >
                    <v-row no-gutters>
                        <v-col cols="12">
                            <v-data-table
                                :headers="subheaders"
                                :items="item.elements"
                                class="elevation-0"
                                :mobile-breakpoint="0"
                                :loading="loading"
                                disable-pagination
                                hide-default-footer
                            >
                                <template v-slot:top>
                                    <v-row
                                        no-gutter
                                        class="mt-0 mx-0 pb-0 align-center"
                                        :style="{
                                            'background-color': '#eeeeee',
                                        }"
                                    >
                                        <v-col
                                            cols="12"
                                            class="d-flex align-center"
                                        >
                                            <h2 class="ml-4">
                                                Elements
                                            </h2>
                                            <v-spacer />
                                        </v-col>
                                    </v-row>
                                </template>
                                <!--ITEMS-->
                                <template
                                    v-slot:[`item.workOrder`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.workOrder || '-' }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.type`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.type || '-' }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.code`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.code || '-' }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.quantity`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.quantity || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.thisDelivery`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.thisDelivery || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.packedQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.packedQty || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.packingProgress`]="{
                                        item,
                                    }"
                                >
                                    <div
                                        class="d-flex flex-column align-center justify-center px-2"
                                    >
                                        <v-progress-linear
                                            :value="
                                                item.packingProgressPercentage ||
                                                    0
                                            "
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.packingProgressPercentage ||
                                                        0
                                                )
                                            "
                                            height="20"
                                            rounded
                                        >
                                            <template v-slot:default>
                                                <strong class="white--text">
                                                    {{
                                                        Math.round(
                                                            item.packingProgressPercentage ||
                                                                0
                                                        )
                                                    }}%
                                                </strong>
                                            </template>
                                        </v-progress-linear>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.pendingWo`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            class="my-0"
                                            :class="{
                                                'font-weight-bold':
                                                    item.pendingWo > 0,
                                                'error--text':
                                                    item.pendingWo > 0,
                                            }"
                                        >
                                            {{ item.pendingWo || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.producedQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                        >
                                            {{ item.producedQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.productionProgress`]="{
                                        item,
                                    }"
                                >
                                    <div
                                        v-if="item.type === 'Work Order'"
                                        class="d-flex flex-column align-center justify-center px-2"
                                    >
                                        <v-progress-linear
                                            :value="
                                                item.productionProgressPercentage ||
                                                    0
                                            "
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.productionProgressPercentage ||
                                                        0
                                                )
                                            "
                                            height="20"
                                            rounded
                                        >
                                            <template v-slot:default>
                                                <strong class="white--text">
                                                    {{
                                                        Math.round(
                                                            item.productionProgressPercentage ||
                                                                0
                                                        )
                                                    }}%
                                                </strong>
                                            </template>
                                        </v-progress-linear>
                                    </div>
                                    <div v-else class="d-flex justify-center">
                                        <p class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.releasedQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                        >
                                            {{ item.releasedQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.releaseProgress`]="{
                                        item,
                                    }"
                                >
                                    <div
                                        v-if="item.type === 'Work Order'"
                                        class="d-flex flex-column align-center justify-center px-2"
                                    >
                                        <v-progress-linear
                                            :value="
                                                item.releaseProgressPercentage ||
                                                    0
                                            "
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.releaseProgressPercentage ||
                                                        0
                                                )
                                            "
                                            height="20"
                                            rounded
                                        >
                                            <template v-slot:default>
                                                <strong class="white--text">
                                                    {{
                                                        Math.round(
                                                            item.releaseProgressPercentage ||
                                                                0
                                                        )
                                                    }}%
                                                </strong>
                                            </template>
                                        </v-progress-linear>
                                    </div>
                                    <div v-else class="d-flex justify-center">
                                        <p class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.pendingQualityQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                            :class="{
                                                'font-weight-bold':
                                                    item.pendingQualityQty > 0,
                                                'warning--text':
                                                    item.pendingQualityQty > 0,
                                            }"
                                        >
                                            {{ item.pendingQualityQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.rejectedQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                            :class="{
                                                'font-weight-bold':
                                                    item.rejectedQty > 0,
                                                'error--text':
                                                    item.rejectedQty > 0,
                                            }"
                                        >
                                            {{ item.rejectedQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.reprocessQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                            :class="{
                                                'font-weight-bold':
                                                    item.reprocessQty > 0,
                                                'warning--text':
                                                    item.reprocessQty > 0,
                                            }"
                                        >
                                            {{ item.reprocessQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.deliveredQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                        >
                                            {{ item.deliveredQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.receivedQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                        >
                                            {{ item.receivedQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.availableQty`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <p
                                            v-if="item.type === 'Work Order'"
                                            class="my-0"
                                            :class="{
                                                'font-weight-bold':
                                                    item.availableQty > 0,
                                                'success--text':
                                                    item.availableQty > 0,
                                            }"
                                        >
                                            {{ item.availableQty || 0 }}
                                        </p>
                                        <p v-else class="my-0">N/A</p>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </td>
            </template>
        </v-data-table>

        <v-dialog
            v-if="calendarView"
            v-model="deleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="deleteLoading" v-if="deleteWarning">
                <v-card-title class="text-h5"
                    >Delete Packing Order</v-card-title
                >
                <v-card-text>
                    Are you sure you want to delete this packing order?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="closeDeletePackingListDialog"
                    >
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="error"
                        :loading="deleteLoading"
                        @click="confirmDeletePackingListFromCalendar"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { firestore } from '@/services/firebase'
import WOMethods from '@/components/WorkOrders/WOMethods.js'
export default {
    name: 'PackingProgress',
    props: {
        event: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
        users: {
            type: Array,
            required: true,
        },
        projects: {
            type: Array,
            required: true,
        },
        calendarView: {
            type: Boolean,
            default: false,
        },
    },
    components: {},
    data: () => ({
        companyId: JSON.parse(localStorage.getItem('company')),
        loading: false,
        expanded: [],
        activeTab: 0,
        headers: [
            {
                text: 'PACKING LIST',
                value: 'packingList',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'CONTAINER',
                value: 'container',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 210,
            },
            {
                text: 'WORK ORDERS',
                value: 'workOrders',
                align: 'center',
                sortable: false,
                width: 160,
            },
            {
                text: 'REQUESTED SHIPMENT DATE',
                value: 'requestedShipmentDate',
                align: 'center',
                sortable: false,
                width: 130,
            },
            {
                text: 'SCHEDULED FOR',
                value: 'scheduledFor',
                align: 'center',
                sortable: false,
                width: 140,
            },
            {
                text: 'AIR SHIPMENT',
                value: 'airShipment',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'STATUS',
                value: 'status',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'PROD. PROGRESS',
                value: 'productionProgress',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'RELEASE PROGRESS',
                value: 'releaseProgress',
                align: 'center',
                sortable: false,
                width: 130,
            },
            {
                text: 'PACK. PROGRESS',
                value: 'packingProgress',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'IMPORTANT',
                value: 'important',
                align: 'center',
                sortable: false,
                width: 100,
            },
        ],
        subheaders: [
            {
                text: 'WORK ORDER',
                value: 'workOrder',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'TYPE',
                value: 'type',
                align: 'center',
                sortable: false,
                width: 80,
            },
            {
                text: 'ITEM CODE',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 150,
            },
            {
                text: 'WO QTY',
                value: 'quantity',
                align: 'center',
                sortable: false,
                width: 80,
            },
            {
                text: 'THIS PL',
                value: 'thisDelivery',
                align: 'center',
                sortable: false,
                width: 80,
            },
            {
                text: 'AVAILABLE',
                value: 'availableQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: 'bg-light-group',
            },

            {
                text: 'PRODUCED',
                value: 'producedQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: 'bg-dark-group',
            },
            {
                text: 'PROD. PROGRESS',
                value: 'productionProgress',
                align: 'center',
                sortable: false,
                width: 120,
                cellClass: 'bg-dark-group',
            },
            {
                text: 'PENDING QA',
                value: 'pendingQualityQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: ' bg-light-group',
            },
            {
                text: 'RELEASED',
                value: 'releasedQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: ' bg-light-group',
            },
            {
                text: 'RELEASE PROGRESS',
                value: 'releaseProgress',
                align: 'center',
                sortable: false,
                width: 120,
                cellClass: ' bg-light-group',
            },
            {
                text: 'PACKED',
                value: 'packedQty',
                align: 'center',
                sortable: false,
                width: 80,
                cellClass: 'bg-dark-group',
            },
            {
                text: 'PACK. PROGRESS',
                value: 'packingProgress',
                align: 'center',
                sortable: false,
                width: 120,
                cellClass: 'bg-dark-group',
            },
            {
                text: 'DELIVERED',
                value: 'deliveredQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: 'bg-light-group',
            },
            {
                text: 'RECEIVED',
                value: 'receivedQty',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: 'bg-light-group',
            },

            {
                text: 'PENDING WO',
                value: 'pendingWo',
                align: 'center',
                sortable: false,
                width: 90,
                cellClass: 'bg-dark-group',
            },
        ],
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        readyForPackingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        workOrders: [],
        packingLists: [],
        workOrdersListener: null,
        packingListsListener: null,
        projectIds: [],
        eventProjects: [],
        completedPackingLists: [],
        completedPackingListsReady: false,
        woMethods: new WOMethods(),
        initialLoad: true,
        deleteWarning: false,
        selectedOrder: null,
        deleteLoading: false,
    }),
    computed: {
        tableHeaders() {
            if (!this.calendarView) {
                return this.headers
            }
            return [
                ...this.headers,
                {
                    text: 'ACTIONS',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 90,
                },
            ]
        },
        eventWorkOrders() {
            // Get all unique work order codes from packing lists
            const workOrderCodes = new Set()
            this.event.elements.forEach(element => {
                if (element.packingList?.selectedWorkOrders) {
                    element.packingList.selectedWorkOrders.forEach(wo => {
                        workOrderCodes.add(wo.code)
                    })
                }
            })
            return Array.from(workOrderCodes)
        },
        sortedPackingLists() {
            // Sort packing lists by creation date (oldest first)
            return [...this.packingLists].sort((a, b) => {
                const dateA = a.createdOn?.seconds || a.createdOn?._seconds || 0
                const dateB = b.createdOn?.seconds || b.createdOn?._seconds || 0
                return dateA - dateB
            })
        },
    },
    async mounted() {
        this.loading = true
        try {
            this.projectIds = [
                ...new Set(
                    this.event.elements.map(
                        element => element.project?.id || element.id
                    )
                ),
            ]
            this.eventProjects = this.projects.filter(project =>
                this.projectIds.includes(project.id)
            )
            await this.loadCompletedPackingLists()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            // Always mark as ready so real-time progress can be computed even
            // if the historical (completed) packing lists failed to load.
            this.completedPackingListsReady = true
        }

        try {
            this.getWorkOrders()
            this.getPackingLists()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.workOrdersListener) {
            this.workOrdersListener()
        }
        if (this.packingListsListener) {
            this.packingListsListener()
        }
        this.workOrdersListener = null
        this.packingListsListener = null
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        updateEventElementsItems() {
            // Wait until closed PLs are loaded; otherwise production % ignores them
            if (!this.completedPackingListsReady) {
                return
            }
            this.event.elements.forEach(element => {
                // Update the packingList reference with the latest version from packingLists
                if (element.packingList && element.packingList.id) {
                    const updatedPackingList = this.packingLists.find(
                        pl => pl.id === element.packingList.id
                    )
                    if (updatedPackingList) {
                        this.$set(element, 'packingList', updatedPackingList)
                    }
                }

                const items = this.plItems(element)
                this.$set(element, 'elements', items)

                // Calculate accumulated progress percentages for the packing list
                if (items.length > 0) {
                    // Calculate production progress (average of all items)
                    const totalProductionProgress = items.reduce(
                        (sum, item) =>
                            sum +
                            (item.type.toLowerCase() === 'work order'
                                ? item.productionProgressPercentage || 0
                                : 100),
                        0
                    )
                    const productionProgressPL =
                        items.length > 0
                            ? totalProductionProgress / items.length
                            : 0

                    // Calculate release progress (average of all items)
                    const totalReleaseProgress = items.reduce(
                        (sum, item) =>
                            sum +
                            (item.type.toLowerCase() === 'work order'
                                ? item.releaseProgressPercentage || 0
                                : 100),
                        0
                    )
                    const releaseProgressPL =
                        items.length > 0
                            ? totalReleaseProgress / items.length
                            : 0

                    // Calculate packing progress (average of all items)
                    const totalPackingProgress = items.reduce(
                        (sum, item) =>
                            sum + (item.packingProgressPercentage || 0),
                        0
                    )
                    const packingProgressPL =
                        items.length > 0
                            ? totalPackingProgress / items.length
                            : 0

                    this.$set(
                        element,
                        'productionProgressPL',
                        productionProgressPL
                    )
                    this.$set(element, 'releaseProgressPL', releaseProgressPL)
                    this.$set(element, 'packingProgressPL', packingProgressPL)
                }
            })
        },
        async getWorkOrders() {
            // Firestore's `in` operator only supports a limited number of
            // values per query, so we split the codes into chunks and merge
            // the results of one listener per chunk into a shared array.
            const FIRESTORE_IN_LIMIT = 10
            let data = []
            this.workOrdersListener = null

            if (!this.eventWorkOrders || this.eventWorkOrders.length === 0) {
                this.workOrders = []
                return
            }

            const chunks = []
            for (
                let i = 0;
                i < this.eventWorkOrders.length;
                i += FIRESTORE_IN_LIMIT
            ) {
                chunks.push(
                    this.eventWorkOrders.slice(i, i + FIRESTORE_IN_LIMIT)
                )
            }

            const unsubscribers = []

            const handleSnapshot = async snapshot => {
                const changes = Array.from(snapshot.docChanges())
                const processedChanges = await Promise.all(
                    changes.map(async change => {
                        let wo = Object.assign(change.doc.data(), {
                            id: change.doc.id,
                        })
                        return { change, wo }
                    })
                )

                processedChanges.forEach(({ change, wo }) => {
                    if (wo) {
                        if (change.type === 'added') {
                            const index = data.findIndex(w => w.id == wo.id)
                            if (index >= 0) {
                                this.$set(data, index, wo)
                            } else {
                                data.push(wo)
                            }
                        }
                        if (change.type === 'modified') {
                            const index = data.findIndex(w => w.id == wo.id)
                            if (index >= 0) {
                                this.$set(data, index, wo)
                            }
                        }
                        if (change.type === 'removed') {
                            const index = data.findIndex(w => w.id == wo.id)
                            if (index >= 0) {
                                data.splice(index, 1)
                            }
                        }
                    }
                    data = data.filter(wo =>
                        this.eventWorkOrders.includes(wo.code)
                    )
                })
                this.workOrders = [...data]

                //validate remaining work orders
                if (
                    this.eventWorkOrders &&
                    this.eventWorkOrders.length > 0 &&
                    this.workOrders.length !== this.eventWorkOrders.length &&
                    this.initialLoad
                ) {
                    try {
                        this.loading = true
                        this.initialLoad = false
                        const workOrderCodes = new Set(
                            this.workOrders.map(wo => wo.code)
                        )

                        // remaining work orders
                        const remainingWorkOrders = [
                            ...new Set(this.eventWorkOrders),
                        ].filter(code => !workOrderCodes.has(code))

                        //find remaining work orders by code
                        const foundWorkOrders = await Promise.all(
                            remainingWorkOrders.map(async code => {
                                const workOrder = await API.findWOByCode(code)
                                return workOrder
                            })
                        )
                        this.workOrders.push(...foundWorkOrders)
                    } catch (error) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    } finally {
                        this.loading = false
                    }
                }
                this.workOrders.forEach(wo => {
                    this.woMethods.formatItems(wo)
                })
                this.updateEventElementsItems()
            }

            // Use allSettled so a failure in one chunk (e.g. a transient
            // Firestore error) doesn't prevent the other chunks from loading.
            const results = await Promise.allSettled(
                chunks.map(async chunkCodes => {
                    const query = firestore
                        .collection('companies')
                        .doc(this.companyId)
                        .collection('workOrders')
                        .where('code', 'in', chunkCodes)
                    return query.onSnapshot(handleSnapshot)
                })
            )
            results.forEach(result => {
                if (result.status === 'fulfilled') {
                    unsubscribers.push(result.value)
                } else {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: result.reason?.message || result.reason,
                    })
                }
            })

            this.workOrdersListener = () => {
                unsubscribers.forEach(unsubscribe => {
                    if (unsubscribe) {
                        unsubscribe()
                    }
                })
            }
        },
        async loadCompletedPackingLists() {
            let oldestCreatedOn = null
            this.eventProjects.forEach(project => {
                if (project.createdOn) {
                    let createdOnDate
                    if (project.createdOn.seconds) {
                        createdOnDate = new Date(
                            project.createdOn.seconds * 1000
                        )
                    } else if (project.createdOn._seconds) {
                        createdOnDate = new Date(
                            project.createdOn._seconds * 1000
                        )
                    }

                    if (
                        createdOnDate &&
                        (!oldestCreatedOn || createdOnDate < oldestCreatedOn)
                    ) {
                        oldestCreatedOn = createdOnDate
                    }
                }
            })

            const currentDate = new Date()
            const date1 = (oldestCreatedOn || currentDate)
                .toISOString()
                .split('T')[0]
            const date2 = currentDate.toISOString().split('T')[0]

            if (this.completedPackingLists.length == 0) {
                try {
                    const completedPackingLists = await API.getPackingLists({
                        status: 'completed',
                        startDate: date1,
                        endDate: date2,
                    })
                    this.completedPackingLists = completedPackingLists.filter(
                        packingList =>
                            this.projectIds.includes(packingList.projectId)
                    )
                } catch (error) {
                    // Do not let a failure fetching the historical (closed)
                    // packing lists block the rest of the component from
                    // loading the active ones.
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                    this.completedPackingLists = []
                }
            }

            const existingIds = new Set(this.packingLists.map(pl => pl.id))
            this.completedPackingLists.forEach(pl => {
                if (pl?.id && !existingIds.has(pl.id)) {
                    this.packingLists.push(pl)
                }
            })
        },
        async getPackingLists() {
            // Active packing lists (closed ones already loaded in mounted)
            let query = firestore
                .collection('companies')
                .doc(this.companyId)
                .collection('packingLists')
            if (this.projectIds && this.projectIds.length > 0) {
                query = query.where('projectId', 'in', this.projectIds)
                this.packingListsListener = await query.onSnapshot(
                    async docSnapshot => {
                        docSnapshot.docChanges().forEach(async change => {
                            const packingList = Object.assign(
                                change.doc.data(),
                                {
                                    id: change.doc.id,
                                }
                            )
                            if (change.type === 'added') {
                                const index = this.packingLists.findIndex(
                                    r => r.id == packingList.id
                                )
                                if (index < 0) {
                                    this.packingLists.splice(0, 0, packingList)
                                }
                            }
                            if (change.type === 'modified') {
                                const index = this.packingLists.findIndex(
                                    r => r.id == packingList.id
                                )
                                if (index >= 0) {
                                    this.packingLists.splice(
                                        index,
                                        1,
                                        packingList
                                    )
                                }
                            }
                            if (change.type === 'removed') {
                                const index = this.packingLists.findIndex(
                                    r => r.id == packingList.id
                                )
                                if (
                                    index >= 0 &&
                                    packingList.status !== 'completed'
                                ) {
                                    this.packingLists.splice(index, 1)
                                }
                            }
                            this.updateEventElementsItems()
                        })
                    }
                )
            } else {
                this.packingLists = []
            }
        },
        plItems(element) {
            let items = []
            if (!element.packingList) {
                console.warn('No packingList in element:', element.name)
                return items
            }
            if (!element.packingList.packages) {
                console.warn(
                    'No packages in packingList:',
                    element.name,
                    element.packingList
                )
                return items
            }

            element.packingList.packages.forEach(packageElement => {
                if (!packageElement.items) {
                    console.warn('No items in package')
                    return
                }

                packageElement.items.forEach(item => {
                    // BOM items don't have an associated work order (they use
                    // item.bom instead of item.workOrder), so they must not
                    // be discarded just because there's no matching entry in
                    // this.workOrders.
                    const isWorkOrderItem = Boolean(item.workOrder)

                    // Find the corresponding work order (only applies to
                    // "Work Order" items)
                    const workOrder = isWorkOrderItem
                        ? this.workOrders.find(
                              wo => wo.code === item.workOrder
                          )
                        : null

                    // If this is a Work Order item whose work order hasn't
                    // loaded yet, skip it for now; it will be recalculated
                    // once the work order data arrives.
                    if (isWorkOrderItem && !workOrder) {
                        return
                    }

                    let woItem = null
                    if (workOrder && workOrder.items) {
                        woItem = workOrder.items.find(
                            i => i.partNumber === (item.partNumber || item.code)
                        )
                    }

                    // Calculate quantities for this item
                    const quantities = this.calculateItemQuantities(
                        item,
                        woItem,
                        element,
                        workOrder
                    )

                    const thisDelivery = ['shipped', 'completed'].includes(
                        element.packingList.status
                    )
                        ? item.packingQty || 0
                        : Math.max(item.suggestedQty || 0, item.packingQty || 0)

                    // Calculate production progress percentage
                    const productionProgressPercentage =
                        thisDelivery > 0
                            ? (quantities.producedQty / thisDelivery) * 100
                            : 0

                    // Calculate packing progress percentage

                    const packedQty = item.packingQty || 0

                    const packingProgressPercentage =
                        thisDelivery > 0 ? (packedQty / thisDelivery) * 100 : 0

                    // Calculate release progress percentage
                    // Items without quality process are considered fully released
                    const hasQualityProcess =
                        woItem &&
                        woItem.processes &&
                        woItem.processes.includes('quality')
                    const releaseProgressPercentage = hasQualityProcess
                        ? thisDelivery > 0
                            ? (quantities.releasedQty / thisDelivery) * 100
                            : 0
                        : thisDelivery > 0
                        ? 100
                        : 0

                    const formatedItem = {
                        code: item.partNumber || item.code,
                        workOrder: item.workOrder || item.bom,
                        type: item.workOrder ? 'Work Order' : 'BOM',
                        quantity: item.quantity || 0,
                        thisDelivery: thisDelivery,
                        packedQty: packedQty,
                        productionProgressPercentage: productionProgressPercentage,
                        packingProgressPercentage: packingProgressPercentage,
                        releaseProgressPercentage: releaseProgressPercentage,
                        ...quantities,
                    }
                    items.push(formatedItem)
                })
            })
            return items
        },
        calculateItemQuantities(packingItem, woItem, element, workOrder) {
            if (!woItem || !workOrder) {
                return {
                    producedQty: 0,
                    deliveredQty: 0,
                    receivedQty: 0,
                    releasedQty: 0,
                    availableQty: 0,
                    pendingWo: packingItem.quantity || 0,
                    rejectedQty: 0,
                    reprocessQty: 0,
                    pendingQualityQty: 0,
                }
            }

            // Calculate total quantities from work order
            const totalProduced = this.calculateProducedQty(woItem)
            const totalDelivered = this.calculateDeliveredToPackingQty(woItem)
            const totalReceived = this.calculateReceivedByPackingQty(woItem)
            const totalReleased = this.calculateReleasedByQualityQty(woItem)
            const totalRejected = this.calculateRejectedByQualityQty(woItem)
            const totalReprocess = this.calculateReprocessByQualityQty(woItem)

            // Calculate quantities already allocated to packing lists created before this one
            const allocatedQty = this.calculateAllocatedQuantities(
                workOrder.code,
                packingItem.partNumber || packingItem.code,
                element.packingList.id
            )

            // Calculate what this packing list needs
            const neededQty = ['shipped', 'completed'].includes(
                element.packingList.status
            )
                ? packingItem.packingQty || 0
                : Math.max(
                      packingItem.suggestedQty || 0,
                      packingItem.packingQty || 0
                  )

            // Calculate remaining available after previous packing lists
            const remainingReceived = Math.max(
                0,
                totalReceived - allocatedQty.received
            )
            const remainingProduced = Math.max(
                0,
                totalProduced - allocatedQty.produced
            )
            const remainingDelivered = Math.max(
                0,
                totalDelivered - allocatedQty.delivered
            )
            const remainingReleased = Math.max(
                0,
                totalReleased - allocatedQty.released
            )
            const remainingRejected = Math.max(
                0,
                totalRejected - allocatedQty.rejected
            )

            const hasQualityProcess =
                woItem.processes && woItem.processes.includes('quality')

            const alreadyPacked = packingItem.packingQty || 0

            // Calculate pending in WO (not suggested or packed in any packing list)
            const totalInPackingLists = this.calculateTotalInPackingLists(
                workOrder.code,
                packingItem.partNumber || packingItem.code
            )
            const pendingWo = Math.max(
                0,
                (packingItem.quantity || 0) - totalInPackingLists
            )

            if (!hasQualityProcess) {
                // Items without quality process are treated as fully released
                return {
                    producedQty: Math.min(neededQty, remainingProduced),
                    deliveredQty: Math.min(neededQty, remainingDelivered),
                    receivedQty: Math.min(neededQty, remainingReceived),
                    releasedQty: Math.min(neededQty, remainingProduced),
                    availableQty: Math.max(
                        0,
                        Math.min(neededQty, remainingProduced) - alreadyPacked
                    ),
                    pendingWo: pendingWo,
                    rejectedQty: 0,
                    reprocessQty: 0,
                    pendingQualityQty: 0,
                }
            }

            // Available for this PL is the minimum between what it needs and what's released by quality
            // Only items released by quality are available for packing
            const availableForThisPL = Math.max(
                0,
                Math.min(neededQty, remainingReleased) - alreadyPacked
            )

            // QAPending = remaining produced - remaining released - remaining rejected
            const remainingInQuality =
                remainingProduced - remainingReleased - remainingRejected
            const pendingQualityQty = Math.min(
                neededQty,
                Math.max(0, remainingInQuality)
            )

            return {
                producedQty: Math.min(neededQty, remainingProduced),
                deliveredQty: Math.min(neededQty, remainingDelivered),
                receivedQty: Math.min(neededQty, remainingReceived),
                releasedQty: Math.min(neededQty, remainingReleased),
                availableQty: availableForThisPL,
                pendingWo: pendingWo,
                rejectedQty: Math.min(neededQty, remainingRejected),
                reprocessQty: Math.max(
                    0,
                    totalReprocess - allocatedQty.reprocess
                ),
                pendingQualityQty: pendingQualityQty,
            }
        },
        calculateProducedQty(woItem) {
            let totalProduced = 0
            if (!woItem.production || !woItem.processes) return 0

            // Get production processes (exclude quality and packing)
            const excludedProcesses = ['quality', 'packing', 'readyForPacking']
            const productionProcesses = woItem.processes.filter(
                processId => !excludedProcesses.includes(processId)
            )

            // Get the last production process
            const lastProductionProcess =
                productionProcesses[productionProcesses.length - 1]

            if (!lastProductionProcess) return 0

            // Sum quantities produced in the last production process
            woItem.production.forEach(entry => {
                if (entry.process.id === lastProductionProcess) {
                    totalProduced += Number(entry.qty || 0)
                }
            })

            return totalProduced
        },
        calculateDeliveredToPackingQty(woItem) {
            let totalDelivered = 0
            if (!woItem.deliveries) return 0

            // Sum quantities delivered to packing process
            woItem.deliveries.forEach(delivery => {
                if (
                    delivery.nextProcess &&
                    delivery.nextProcess.id === this.packingProcess.id
                ) {
                    totalDelivered += Number(delivery.qty || 0)
                }
            })

            return totalDelivered
        },
        calculateReceivedByPackingQty(woItem) {
            let totalReceived = 0
            if (!woItem.deliveries) return 0

            // Sum quantities received by packing process
            woItem.deliveries.forEach(delivery => {
                if (
                    delivery.nextProcess &&
                    delivery.nextProcess.id === this.packingProcess.id &&
                    delivery.receivedBy
                ) {
                    totalReceived += Number(delivery.qty || 0)
                }
            })

            return totalReceived
        },
        calculateReleasedByQualityQty(woItem) {
            let totalReleased = 0
            if (!woItem.quality) return 0

            woItem.quality.forEach(entry => {
                totalReleased += Number(entry.releaseQty || 0)
            })

            return totalReleased
        },
        calculateRejectedByQualityQty(woItem) {
            let totalRejected = 0
            if (!woItem.quality) return 0

            woItem.quality.forEach(entry => {
                totalRejected += Number(entry.rejectQty || 0)
            })

            return totalRejected
        },
        calculateReprocessByQualityQty(woItem) {
            let totalReprocess = 0
            if (!woItem.quality) return 0

            woItem.quality.forEach(entry => {
                totalReprocess += Number(entry.reprocessQty || 0)
            })

            return totalReprocess
        },
        calculateAllocatedQuantities(workOrderCode, partNumber, currentPLId) {
            // Older PLs reserve qty first. Closed/shipped PLs always consume their
            // packed qty so an active PL cannot reuse production already packed.
            let allocated = {
                produced: 0,
                delivered: 0,
                received: 0,
                released: 0,
                rejected: 0,
                reprocess: 0,
            }

            const currentPLIndex = this.sortedPackingLists.findIndex(
                pl => pl.id === currentPLId
            )

            this.sortedPackingLists.forEach((pl, index) => {
                if (pl.id === currentPLId || !pl.packages) {
                    return
                }

                const isClosed = ['shipped', 'completed'].includes(pl.status)
                const isBeforeCurrent =
                    currentPLIndex >= 0 && index < currentPLIndex

                // Logical fix: closed PLs must always discount, even if sort order
                // would place them after the active PL.
                if (!isClosed && !isBeforeCurrent) {
                    return
                }

                pl.packages.forEach(pkg => {
                    ;(pkg.items || []).forEach(item => {
                        if (
                            (item.workOrder === workOrderCode ||
                                item.bom === workOrderCode) &&
                            (item.partNumber === partNumber ||
                                item.code === partNumber)
                        ) {
                            const allocatedQty = isClosed
                                ? item.packingQty || 0
                                : Math.max(
                                      item.suggestedQty || 0,
                                      item.packingQty || 0
                                  )

                            allocated.produced += allocatedQty
                            allocated.delivered += allocatedQty
                            allocated.received += allocatedQty
                            allocated.released += allocatedQty
                        }
                    })
                })
            })

            return allocated
        },
        calculateTotalInPackingLists(workOrderCode, partNumber) {
            // Calculate total quantity in all packing lists for this item
            let total = 0

            this.packingLists.forEach(pl => {
                if (!pl.packages) return

                pl.packages.forEach(pkg => {
                    ;(pkg.items || []).forEach(item => {
                        if (
                            (item.workOrder === workOrderCode ||
                                item.bom === workOrderCode) &&
                            (item.partNumber === partNumber ||
                                item.code === partNumber)
                        ) {
                            const qty = ['shipped', 'completed'].includes(
                                pl.status
                            )
                                ? item.packingQty || 0
                                : Math.max(
                                      item.suggestedQty || 0,
                                      item.packingQty || 0
                                  )
                            total += qty
                        }
                    })
                })
            })

            return total
        },
        getUserName(userId) {
            const foundUser = this.users.find(u => u.id == userId)
            return foundUser ? foundUser.name : ''
        },
        close() {
            this.$emit('close')
        },
        goToPackingBalance() {
            this.$router.push({ name: 'packing-balance' })
        },
        getProgressColor(key, percentage) {
            if (key == 'count') return 'primary'
            if (percentage < 25) return 'error'
            if (percentage < 50) return 'orange'
            if (percentage < 75) return 'warning'
            if (percentage < 100) return 'info'
            return 'success'
        },
        calculateItemProgress(item) {
            item.progressData = {}
            const excludedProcesses = ['quality', 'packing', 'readyForPacking']

            // Get all production processes for this item
            const productionProcesses = item.processes
                ? item.processes.filter(
                      processId => !excludedProcesses.includes(processId)
                  )
                : []

            if (item.production) {
                item.production.forEach(entry => {
                    const entryProcess = entry.process.id

                    // Only count production processes, exclude quality and packing
                    if (!excludedProcesses.includes(entryProcess)) {
                        // initialize if not exist
                        if (!item.progressData[entryProcess]) {
                            item.progressData[entryProcess] = 0
                        }
                        item.progressData[entryProcess] += Number(entry.qty)
                    }
                })
            }

            const totalPossibleProduction =
                item.quantity * productionProcesses.length
            let totalProduced = 0

            // Sum all produced quantities across all production processes
            Object.values(item.progressData).forEach(qty => {
                totalProduced += Number(qty)
            })

            // Calculate percentage
            item.progress =
                totalPossibleProduction > 0
                    ? (totalProduced / totalPossibleProduction) * 100
                    : 0
        },
        calculateReleasedQty(workOrder) {
            let totalReleased = 0
            if (workOrder.items) {
                workOrder.items.forEach(item => {
                    if (item.quality) {
                        item.quality.forEach(entry => {
                            totalReleased += Number(entry.releaseQty || 0)
                        })
                    }
                })
            }
            return totalReleased
        },
        canToggleImportant(item) {
            // Only production planners can toggle important flag
            if (!this.user.permissions.includes('updateWOProcesses')) {
                return false
            }
            // Cannot toggle if status is shipped or completed
            if (
                item.packingList.status === 'shipped' ||
                item.packingList.status === 'completed'
            ) {
                return false
            }
            return true
        },
        async toggleImportant(item) {
            try {
                this.$set(item, 'loadingImportant', true)
                const newImportantValue = !item.packingList.important
                await API.updatePackingList({
                    projectId: item.project.id,
                    packingListId: item.packingList.id,
                    important: newImportantValue,
                })
                // Update local state
                item.packingList.important = newImportantValue

                // Emit event to parent with updated data
                this.$emit('packingListUpdated', {
                    packingListId: item.packingList.id,
                    important: newImportantValue,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.$set(item, 'loadingImportant', false)
            }
        },
        openDeletePackingListDialog(item) {
            if (!this.calendarView) return
            this.selectedOrder = _.cloneDeep(item)
            this.deleteWarning = true
        },
        closeDeletePackingListDialog() {
            this.selectedOrder = null
            this.deleteWarning = false
        },
        async confirmDeletePackingListFromCalendar() {
            const row = this.selectedOrder
            const pl = row?.packingList
            if (!pl?.id) {
                this.closeDeletePackingListDialog()
                return
            }
            const projectId = pl.projectId || row?.project?.id
            if (!projectId) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Missing project for this packing list.',
                })
                return
            }
            const deletedId = pl.id
            try {
                this.deleteLoading = true
                await API.deletePackingList({
                    id: deletedId,
                    projectId: projectId,
                })
                this.closeDeletePackingListDialog()
                this.$emit('packingListDeleted', { packingListId: deletedId })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.deleteLoading = false
            }
        },
        /**
         * Determines the status of a packing list based on its conditions
         * @param {Object} pl - The packing list object
         * @param {Object} plProject - The project associated with the packing list
         * @returns {string} - The determined status or original status if no conditions match
         */
        getPLStatus(pl, plProject) {
            // Check if packing list meets the basic conditions for status change
            const hasNoPackages = !pl.packages || pl.packages.length === 0
            const hasScheduledDate = pl.scheduledFor || pl.requestedShipmentDate
            const isInProgress = pl.status === 'inProgress'

            if (!hasNoPackages || !hasScheduledDate || !isInProgress) {
                return pl.status // Return original status if conditions don't match
            }

            const isDomesticShipping =
                plProject.country &&
                plProject.country.toLowerCase() === this.countryName

            // Case 1: Has selected work orders
            if (pl.selectedWorkOrders) {
                return isDomesticShipping ? 'domesticShipping' : 'reserved'
            }

            // Case 2: No selected work orders
            if (!pl.selectedWorkOrders) {
                return isDomesticShipping
                    ? 'domesticShipping'
                    : 'reservedForProject'
            }

            // Return original status if no conditions match
            return pl.status
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}
.v-data-table {
    border: 1px solid #eeeeee;
}
.bg-light-group {
    background-color: #ffffff !important;
}
.bg-dark-group {
    background-color: #e0e0e0 !important;
}
</style>
